import { useTranslation } from 'react-i18next';

export default function TermsOfService({ onBack }) {
  const { t } = useTranslation();

  return (
    <div className="min-h-[100dvh] bg-[var(--aif-bg-page)] text-[var(--aif-text-primary)]">
      <div className="max-w-3xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-8">
          {onBack && (
            <button
              onClick={onBack}
              className="p-2 rounded-lg hover:bg-[var(--aif-bg-surface)] transition"
              aria-label={t('onboarding.back')}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
          )}
          <h1 className="text-2xl font-bold">Allgemeine Gesch&auml;ftsbedingungen (AGB)</h1>
        </div>

        <div className="prose dark:prose-invert max-w-none space-y-6">
          {/* 1. Geltungsbereich */}
          <section>
            <h2 className="text-xl font-semibold mb-3">1. Geltungsbereich</h2>
            <p>
              Diese Allgemeinen Gesch&auml;ftsbedingungen (nachfolgend &bdquo;AGB&ldquo;) gelten f&uuml;r
              die Nutzung der KI-gest&uuml;tzten Plattform &bdquo;AI Farm&ldquo; (nachfolgend &bdquo;Dienst&ldquo;),
              bereitgestellt von:
            </p>
            <p>
              <strong>ALCEDO Management GmbH</strong><br />
              (nachfolgend &bdquo;Betreiber&ldquo; oder &bdquo;wir&ldquo;)
            </p>
            <p>
              Der Vertrieb und die Lizenzierung f&uuml;r den DACH-Raum (Deutschland, &Ouml;sterreich, Schweiz)
              erfolgt durch die <strong>Airfarm GmbH</strong> als exklusiver Lizenznehmer.
            </p>
            <p>
              Mit der Registrierung und Nutzung des Dienstes akzeptiert der Nutzer diese AGB.
              Abweichende Bedingungen des Nutzers werden nicht anerkannt.
            </p>
          </section>

          {/* 2. Leistungsbeschreibung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">2. Leistungsbeschreibung</h2>
            <p>
              AI Farm ist ein KI-CoPilot f&uuml;r landwirtschaftliche Betriebe. Der Dienst umfasst:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>KI-basierte Beratung zu Ackerbau, Tierhaltung, Energie und Immobilien</li>
              <li>Wettervorhersagen und Spritzfenster-Berechnung (basierend auf DWD-Daten)</li>
              <li>F&ouml;rder-Radar: Abgleich von F&ouml;rderprogrammen mit dem Betriebsprofil</li>
              <li>Dokumentenanalyse und automatische Datenextraktion</li>
              <li>Betriebsdaten-Verwaltung und Compliance-Unterst&uuml;tzung</li>
            </ul>
            <p>
              Der Dienst stellt Entscheidungshilfen bereit. Er ersetzt keine fachliche Beratung
              durch qualifizierte Agrarberatung, Steuerberatung oder Rechtsberatung.
            </p>
          </section>

          {/* 3. Nutzerkonto */}
          <section>
            <h2 className="text-xl font-semibold mb-3">3. Nutzerkonto und Registrierung</h2>
            <p>
              Die Nutzung des Dienstes erfordert ein Nutzerkonto. Bei der Registrierung m&uuml;ssen
              wahrheitsgem&auml;&szlig;e Angaben gemacht werden. Der Nutzer ist f&uuml;r die Geheimhaltung
              seiner Zugangsdaten verantwortlich.
            </p>
            <p>
              Jeder Nutzer darf nur ein Konto f&uuml;hren. Das Konto ist nicht &uuml;bertragbar.
              Der Betreiber beh&auml;lt sich das Recht vor, Konten bei Versto&szlig; gegen diese AGB
              zu sperren oder zu l&ouml;schen.
            </p>
          </section>

          {/* 4. Abonnement und Zahlung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">4. Abonnement und Zahlung</h2>
            <p>
              Der Dienst wird in verschiedenen Abonnementstufen angeboten (Free, Professional, Enterprise).
              Die jeweils g&uuml;ltigen Preise und Leistungsumf&auml;nge sind auf der Website einsehbar.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Kostenpflichtige Abonnements verl&auml;ngern sich automatisch um die jeweilige Laufzeit,
                sofern sie nicht vor Ablauf gek&uuml;ndigt werden.</li>
              <li>Die Zahlung erfolgt &uuml;ber Stripe Inc. per Kreditkarte oder SEPA-Lastschrift.</li>
              <li>Rechnungen werden elektronisch bereitgestellt.</li>
              <li>Bei Zahlungsverzug beh&auml;lt sich der Betreiber das Recht vor, den Zugang
                zum kostenpflichtigen Funktionsumfang einzuschr&auml;nken.</li>
            </ul>
            <p>
              Preise verstehen sich zzgl. der gesetzlichen Mehrwertsteuer, sofern nicht anders angegeben.
            </p>
          </section>

          {/* 5. Datenschutz */}
          <section>
            <h2 className="text-xl font-semibold mb-3">5. Datenschutz</h2>
            <p>
              Die Verarbeitung personenbezogener Daten erfolgt gem&auml;&szlig; unserer
              Datenschutzerkl&auml;rung, die unter{' '}
              <a href="/datenschutz" className="text-[var(--aif-color-primary)] hover:underline">
                /datenschutz
              </a>{' '}
              abrufbar ist. Die Datenschutzerkl&auml;rung ist Bestandteil dieser AGB.
            </p>
            <p>
              Betriebsdaten werden verschl&uuml;sselt gespeichert und ausschlie&szlig;lich auf Servern
              in Deutschland (Hetzner Online GmbH) verarbeitet. Mandantentrennung (Multi-Tenancy)
              gew&auml;hrleistet, dass kein Nutzer auf Daten anderer Nutzer zugreifen kann.
            </p>
          </section>

          {/* 6. Haftung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">6. Haftungsbeschr&auml;nkung</h2>
            <p>
              Der Betreiber haftet unbeschr&auml;nkt f&uuml;r Vorsatz und grobe Fahrl&auml;ssigkeit.
              F&uuml;r leichte Fahrl&auml;ssigkeit haftet der Betreiber nur bei Verletzung wesentlicher
              Vertragspflichten (Kardinalpflichten), begrenzt auf den vorhersehbaren,
              vertragstypischen Schaden.
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>KI-generierte Empfehlungen stellen keine verbindliche Fachberatung dar.
                Der Nutzer handelt auf eigenes Risiko.</li>
              <li>Die Haftung f&uuml;r Datenverlust ist auf den typischen Wiederherstellungsaufwand
                beschr&auml;nkt, der bei ordnungsgem&auml;&szlig;er Datensicherung entstanden w&auml;re.</li>
              <li>Wetterdaten stammen vom Deutschen Wetterdienst (DWD). F&uuml;r deren Richtigkeit
                &uuml;bernimmt der Betreiber keine Gew&auml;hr.</li>
              <li>F&ouml;rderinformationen dienen der Orientierung und ersetzen keine offizielle
                F&ouml;rderberatung.</li>
            </ul>
          </section>

          {/* 7. Verf&uuml;gbarkeit */}
          <section>
            <h2 className="text-xl font-semibold mb-3">7. Verf&uuml;gbarkeit</h2>
            <p>
              Der Betreiber ist bem&uuml;ht, eine Verf&uuml;gbarkeit des Dienstes von 99% im Jahresmittel
              zu gew&auml;hrleisten. Hiervon ausgenommen sind geplante Wartungsfenster, h&ouml;here Gewalt
              und St&ouml;rungen au&szlig;erhalb des Einflussbereichs des Betreibers.
            </p>
          </section>

          {/* 8. Nutzungsrechte und geistiges Eigentum */}
          <section>
            <h2 className="text-xl font-semibold mb-3">8. Nutzungsrechte und geistiges Eigentum</h2>
            <p>
              Der Nutzer erh&auml;lt ein einfaches, nicht &uuml;bertragbares Recht zur Nutzung des Dienstes
              f&uuml;r die Dauer des Vertragszeitraums. Alle Rechte an der Software, den Algorithmen
              und der Plattform verbleiben bei der ALCEDO Management GmbH.
            </p>
            <p>
              Vom Nutzer eingegebene Betriebsdaten verbleiben im Eigentum des Nutzers.
              Der Nutzer r&auml;umt dem Betreiber das Recht ein, diese Daten zur Erbringung und
              Verbesserung des Dienstes in anonymisierter Form zu nutzen.
            </p>
          </section>

          {/* 9. K&uuml;ndigung */}
          <section>
            <h2 className="text-xl font-semibold mb-3">9. K&uuml;ndigung</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><strong>Free-Tarif:</strong> Jederzeit durch Kontol&ouml;schung k&uuml;ndbar.</li>
              <li><strong>Kostenpflichtige Tarife:</strong> K&uuml;ndigung zum Ende der jeweiligen
                Abrechnungsperiode &uuml;ber das Stripe Customer Portal oder per E-Mail.</li>
              <li><strong>Au&szlig;erordentliche K&uuml;ndigung:</strong> Beiden Parteien steht das Recht
                zur au&szlig;erordentlichen K&uuml;ndigung aus wichtigem Grund zu.</li>
            </ul>
            <p>
              Nach K&uuml;ndigung werden die Nutzerdaten gem&auml;&szlig; Datenschutzerkl&auml;rung
              anonymisiert oder gel&ouml;scht. Ein Datenexport ist bis zum Ablauf des Vertragszeitraums
              m&ouml;glich.
            </p>
          </section>

          {/* 10. &Auml;nderungen der AGB */}
          <section>
            <h2 className="text-xl font-semibold mb-3">10. &Auml;nderungen der AGB</h2>
            <p>
              Der Betreiber beh&auml;lt sich das Recht vor, diese AGB mit angemessener Frist zu
              &auml;ndern. &Auml;nderungen werden dem Nutzer per E-Mail oder In-App-Benachrichtigung
              mitgeteilt. Widerspricht der Nutzer nicht innerhalb von 30 Tagen, gelten die
              ge&auml;nderten AGB als akzeptiert.
            </p>
          </section>

          {/* 11. Schlussbestimmungen */}
          <section>
            <h2 className="text-xl font-semibold mb-3">11. Schlussbestimmungen</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>Es gilt das Recht der Bundesrepublik Deutschland unter Ausschluss des
                UN-Kaufrechts (CISG).</li>
              <li>Gerichtsstand f&uuml;r alle Streitigkeiten ist Hamburg, sofern der Nutzer Kaufmann,
                juristische Person des &ouml;ffentlichen Rechts oder &ouml;ffentlich-rechtliches
                Sonderverm&ouml;gen ist.</li>
              <li>Sollten einzelne Bestimmungen dieser AGB unwirksam sein, bleibt die Wirksamkeit
                der &uuml;brigen Bestimmungen unber&uuml;hrt.</li>
            </ul>
          </section>

          <p className="text-sm text-[var(--aif-text-muted)] mt-8">
            Stand: M&auml;rz 2026
          </p>
        </div>
      </div>
    </div>
  );
}
